import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import {
  Activity,
  BadgeCheck,
  Ban,
  CircleCheck,
  CircleX,
  Clock,
  Coins,
  Crown,
  FileCheck2,
  FilePlus2,
  FileX2,
  Flag,
  HandCoins,
  Layers,
  LogIn,
  Pause,
  Pencil,
  Send,
  Trash2,
  UserMinus,
  UserPlus,
  UserX,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { relativeTime } from "@/shared/lib/time";
import { formatNum } from "@/features/metrics/format";
import { useCustomerActivity } from "../hooks";
import type { ActivityEvent, ActivityPeriod } from "../types";
import { SectionTitle } from "./fields";

// ── Customer activity feed (task 0153) ───────────────────────────────────────
// What the customer did themselves in the merchant / client apps (ActionLog rows),
// split into last 5 / last 7 days / all time. Labels come from the action constants in
// apps/logs/constants.py; the detail line is built from target_ct + extra, never from
// target_repr (Loan has no __str__) or description (English, internal).

const PERIODS: ActivityPeriod[] = ["last5", "7d", "all"];

const ACTION_ICONS: Record<string, LucideIcon> = {
  "user.login": LogIn,
  "user.register": UserPlus,
  "user.client_add": UserPlus,
  "user.client_remove": UserMinus,
  "user.profile_update": Pencil,
  "user.delete": UserX,
  "loan.create": FilePlus2,
  "loan.verify": Send,
  "loan.activate": FileCheck2,
  "loan.reject": FileX2,
  "loan.pause": Pause,
  "loan.cancel": Ban,
  "loan.finish": Flag,
  "loan.delete": Trash2,
  "payment.approve": CircleCheck,
  "payment.reject": CircleX,
  "payment.delay": Clock,
  "payment.several_create": Layers,
  "payment.mark_paid": BadgeCheck,
  "debt.cash_update": Wallet,
  "debt.settle": HandCoins,
};

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  payment: Coins,
  premium: Crown,
  debt: HandCoins,
  loan: FilePlus2,
};

// Icon chip tint per category — money green, agreements blue, premium amber, debts red.
const CATEGORY_TONE: Record<string, string> = {
  payment: "bg-green-bg text-green-dark",
  loan: "bg-blue-bg-soft text-blue",
  premium: "bg-warning-bg text-warning",
  debt: "bg-red-bg text-red",
};

function asNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function asString(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/** Second line of a row: agreement / payment / client reference plus the useful `extra` bits. */
function activityDetails(e: ActivityEvent, t: TFunction): string[] {
  const x = e.extra ?? {};
  const out: string[] = [];

  const loanId = asNumber(x.loan_id) ?? (e.target_ct === "loans.Loan" ? e.target_id : null);
  if (loanId !== null) out.push(t("activity.agreement", { id: loanId }));
  else if (e.target_ct === "payment.Payment" && e.target_id !== null) out.push(t("activity.payment", { id: e.target_id }));
  else if (e.target_ct === "debts.Debt" && e.target_id !== null) out.push(t("activity.debt", { id: e.target_id }));

  const clientId = asNumber(x.client_id);
  if (clientId !== null) out.push(t("activity.client", { code: `NS-${String(clientId).padStart(5, "0")}` }));

  // `type` is logged uppercase (FULL / CLOSE_PART), `payment_type` is the raw lowercase
  // PaymentType value — normalise both onto the uppercase keys.
  if (e.category === "payment") {
    const type = (asString(x.type) ?? asString(x.payment_type))?.toUpperCase();
    if (type) out.push(t(`activity.paymentType.${type}`, { defaultValue: type }));
  }

  const amount = asNumber(x.amount);
  if (amount !== null) out.push(t("activity.amount", { amount: formatNum(Math.round(amount)) }));
  const cash = asNumber(x.cash);
  if (cash !== null) out.push(t("activity.cash", { amount: formatNum(Math.round(cash)) }));

  const count = asNumber(x.count);
  if (count !== null) out.push(t("activity.months", { count }));

  const status = asString(x.new_status)?.toLowerCase();
  if (status) {
    out.push(t("activity.newStatus", { status: t(`activity.loanStatus.${status}`, { defaultValue: status }) }));
  }
  if (x.next_month === true) out.push(t("activity.nextMonth"));
  if (x.auto_approved === true) out.push(t("activity.autoApproved"));
  return out;
}

function dayLabel(d: Date, t: TFunction): string {
  if (isToday(d)) return t("activity.today");
  if (isYesterday(d)) return t("activity.yesterday");
  return format(d, "dd.MM.yyyy");
}

/** Consecutive same-day runs (events already arrive newest first). */
function groupByDay(events: ActivityEvent[]): { day: string; date: Date; events: ActivityEvent[] }[] {
  const groups: { day: string; date: Date; events: ActivityEvent[] }[] = [];
  for (const e of events) {
    const date = parseISO(e.timestamp);
    const day = format(date, "yyyy-MM-dd");
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.events.push(e);
    else groups.push({ day, date, events: [e] });
  }
  return groups;
}

export function ActivityFeed({ customerId }: { customerId: number }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<ActivityPeriod>("last5");
  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useCustomerActivity(customerId, period);

  const events = data?.pages.flatMap((p) => p.results) ?? [];
  const total = data?.pages[0]?.count ?? 0;

  return (
    <section className="mb-7">
      <SectionTitle>{t("activity.title")}</SectionTitle>

      <div className="mb-4 flex items-center gap-3">
        <div role="tablist" aria-label={t("activity.title")} className="inline-flex rounded-control bg-grey-100 p-0.5">
          {PERIODS.map((p) => {
            const active = period === p;
            return (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPeriod(p)}
                className={`h-8 rounded-[7px] px-3 text-[12.5px] font-semibold transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green/40 ${
                  active ? "bg-white text-grey-900 shadow-[0_1px_2px_rgba(16,24,40,0.08)]" : "text-grey-500 hover:text-grey-800"
                }`}
              >
                {t(`activity.period.${p}`)}
              </button>
            );
          })}
        </div>
        {period !== "last5" && !isLoading && !isError && (
          <span className="tnum text-[12.5px] text-grey-400">{t("activity.total", { count: total })}</span>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3].map((k) => (
            <div key={k} className="flex items-start gap-3">
              <div className="h-7 w-7 flex-none animate-pulse rounded-pill bg-grey-100" />
              <div className="flex-1">
                <div className="h-3.5 w-40 animate-pulse rounded bg-grey-100" />
                <div className="mt-2 h-3 w-24 animate-pulse rounded bg-grey-100" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-border bg-red-bg px-3.5 py-3">
          <p className="flex-1 text-[13px] font-medium text-red">{t("activity.loadError")}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="h-8 rounded-control border border-red-border bg-white px-3 text-[12.5px] font-semibold text-red hover:bg-red-bg"
          >
            {t("activity.retry")}
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-grey-200 px-4 py-8 text-center">
          <span className="mb-2.5 flex h-10 w-10 items-center justify-center rounded-pill bg-grey-50 text-grey-400">
            <Activity size={20} strokeWidth={1.8} />
          </span>
          <p className="text-[13.5px] font-semibold text-grey-700">{t("activity.empty")}</p>
          <p className="mt-0.5 text-[12.5px] text-grey-500">{t(`activity.emptyHint.${period}`)}</p>
        </div>
      ) : (
        <>
          {groupByDay(events).map((g) => (
            <div key={g.day} className="mb-4 last:mb-0">
              <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-grey-400">
                {dayLabel(g.date, t)}
              </div>
              <ul className="flex flex-col">
                {g.events.map((e, i) => (
                  <ActivityRow key={e.id} event={e} last={i === g.events.length - 1} index={i} />
                ))}
              </ul>
            </div>
          ))}

          {hasNextPage && (
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="mt-2 flex h-9 w-full items-center justify-center rounded-control border border-grey-200 bg-white text-[13px] font-semibold text-grey-700 transition hover:border-grey-300 hover:bg-grey-25 active:scale-[0.99] disabled:opacity-60"
            >
              {isFetchingNextPage ? t("app.loading") : t("activity.loadMore")}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function ActivityRow({ event: e, last, index }: { event: ActivityEvent; last: boolean; index: number }) {
  const { t } = useTranslation();
  const Icon = ACTION_ICONS[e.action] ?? CATEGORY_ICONS[e.category] ?? Activity;
  const tone = CATEGORY_TONE[e.category] ?? "bg-grey-100 text-grey-600";
  const label = t(`activity.actions.${e.action}`, {
    defaultValue: t(`activity.categories.${e.category}`, { defaultValue: e.action }),
  });
  const details = activityDetails(e, t);
  const reason = asString(e.extra?.reject_cause);
  const at = parseISO(e.timestamp);

  return (
    <li
      className="relative flex items-start gap-3 pb-3.5 last:pb-0 animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-both duration-300"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      {/* Rail connecting the icon chips within one day. */}
      {!last && <span aria-hidden className="absolute bottom-0 left-[13.5px] top-8 w-px bg-grey-100" />}
      <span className={`relative mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-pill ${tone}`}>
        <Icon size={14} strokeWidth={2} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[13.5px] font-semibold text-grey-900">{label}</span>
            {e.app && (
              <span
                className={`rounded-pill px-1.5 py-[1px] text-[11px] font-semibold ${
                  e.app === "client" ? "bg-blue-bg-soft text-blue" : "bg-grey-100 text-grey-600"
                }`}
              >
                {t(`activity.app.${e.app}`)}
              </span>
            )}
          </div>
          <time
            dateTime={e.timestamp}
            title={`${format(at, "dd.MM.yyyy HH:mm")} · ${relativeTime(e.timestamp, t)}`}
            className="tnum flex-none pt-[1px] text-[12px] text-grey-400"
          >
            {format(at, "HH:mm")}
          </time>
        </div>
        {details.length > 0 && (
          <div className="tnum mt-0.5 text-[12.5px] text-grey-500">{details.join(" · ")}</div>
        )}
        {reason && (
          <div className="mt-1 border-l-2 border-grey-200 pl-2 text-[12.5px] italic text-grey-600">
            {t("activity.reason", { reason })}
          </div>
        )}
      </div>
    </li>
  );
}
